import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/constants";
import { PiggyBank, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function SurplusSweep({ forecast, onApplied }) {
  const [prog, setProg] = useState(null);
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);
  const sweep = forecast?.sweepable_monthly || 0;

  useEffect(() => {
    if (sweep <= 0) { setProg(null); setSim(null); return; }
    let alive = true;
    api.get("/debts/progress").then(r => {
      if (!alive) return;
      setProg(r.data);
      const snow = r.data.plan.snowball_amount || 0;
      if (!r.data.totals.projected_months) return null;
      return api.post("/debts/simulate", { strategy: r.data.plan.strategy, extra_monthly_payment: snow + sweep });
    }).then(r => { if (alive && r) setSim(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [sweep]);

  if (sweep <= 0 || !prog || !prog.totals.projected_months) return null;

  const snow = prog.plan.snowball_amount || 0;
  const curMonths = prog.totals.projected_months;
  const newMonths = sim?.chosen.totals.months_to_debt_free;
  const monthsSaved = newMonths != null ? curMonths - newMonths : null;
  const interestSaved = sim ? Math.max(0, prog.totals.projected_interest - sim.chosen.totals.total_interest) : null;
  const newDate = sim?.chosen.totals.payoff_date;

  const apply = async () => {
    setBusy(true);
    try {
      await api.put("/payoff-plan", { snowball_amount: snow + sweep });
      toast.success(`Snowball boosted to ${fmt(snow + sweep)}/mo`, { description: "Your plan and calendar now reflect the extra payment." });
      onApplied?.();
    } catch { toast.error("Could not update plan"); } finally { setBusy(false); }
  };

  return (
    <div className="p-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/5" data-testid="surplus-sweep">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-500/15 text-emerald-500 flex items-center justify-center shrink-0"><PiggyBank className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-lg">You have <span className="text-emerald-600 dark:text-emerald-400" data-testid="surplus-amount">{fmt(sweep)}/mo</span> of spare cash</div>
          <p className="text-sm text-muted-foreground mt-1">
            Your forecast stays positive every month even after adding this much to debt. Sweep it into your {prog.plan.strategy} plan to finish faster.
          </p>
          {sim && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <div><span className="text-muted-foreground">Debt-free by </span><span className="font-mono font-bold">{newDate ? fmtDate(newDate) : "—"}</span></div>
              {monthsSaved > 0 && <div className="text-emerald-600 dark:text-emerald-400 font-semibold" data-testid="surplus-months-saved">{monthsSaved} month{monthsSaved === 1 ? "" : "s"} sooner</div>}
              {interestSaved > 0 && <div className="text-emerald-600 dark:text-emerald-400 font-semibold" data-testid="surplus-interest-saved">Save {fmt(interestSaved)} interest</div>}
            </div>
          )}
        </div>
      </div>
      <button onClick={apply} disabled={busy} data-testid="surplus-apply-btn"
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition disabled:opacity-50">
        Boost snowball to {fmt(snow + sweep)}/mo <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
